import { container } from 'tsyringe'
import { Bodies, Body, IChamferableBodyDefinition, Query } from 'matter-js'
import { Camera, Material, Mesh, MeshBasicMaterial, Plane, Raycaster, Vector2, Vector3 } from 'three'
import { ColliderType, MATTER_SCALE, MATTER_TO_THREE_SCALE, PhysicsLayerZ } from './constants'
import { BodyType, CollisionCategory, CollisionGroup } from '../rooms/room-physics-base'
import { Interface } from '@/shared/di/interface'
import type { ISize } from '@/shared/di/interface/size'

const tempVector2 = new Vector2()
const tempVector3 = new Vector3()

const raycastPlane = new Plane(new Vector3(0, 0, 1), 0)
const raycastPlaneZFront = new Plane(new Vector3(0, 0, 1), -1 * PhysicsLayerZ.Front)
const raycaster = new Raycaster()

export const colliderMaterial = new MeshBasicMaterial({
  color: 0xff0000,
  wireframe: false,
  depthTest: false,
  depthWrite: false,
  transparent: true
})

export default class SceneUtils {
  static screenToSceneSpace(camera: Camera, screenPos: Vector2, layerZ: PhysicsLayerZ): Vector3 {
    //TODO: If called often, send in vector to write to. Avoiding for now
    //because of readability

    const result = new Vector3()
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    const clipX = (screenPos.x / size.width) * 2 - 1
    const clipY = -(screenPos.y / size.height) * 2 + 1
    tempVector2.set(clipX, clipY)

    camera.parent?.updateMatrixWorld(true)
    raycaster.setFromCamera(tempVector2, camera)
    raycastPlane.constant = -layerZ
    raycaster.ray.intersectPlane(raycastPlane, result)

    result.y *= -1

    return result
  }

  static getBodyPositionInScene(body: Body) {
    return tempVector3.set(
      body.position.x * MATTER_TO_THREE_SCALE,
      -body.position.y * MATTER_TO_THREE_SCALE,
      body.collisionFilter.group === CollisionGroup.ActiveCoins ? PhysicsLayerZ.Front : 0
    )
  }

  static matterToSceneAtZ0(pos: Vector2) {
    return tempVector3.set(pos.x * MATTER_TO_THREE_SCALE, -pos.y * MATTER_TO_THREE_SCALE, 0)
  }

  static sceneToScreenSpace(camera: Camera, scenePos: Vector3): Vector2 {
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    const widthHalf = size.width / 2
    const heightHalf = size.height / 2

    camera.parent?.updateMatrixWorld(true)
    const pos = scenePos.clone()
    pos.project(camera)

    pos.x = pos.x * widthHalf + widthHalf
    pos.y = -(pos.y * heightHalf) + heightHalf

    return new Vector2(pos.x, pos.y)
  }

  static raycastBodies(camera: Camera, bodies: Body[], touch: Touch, layerZ = 0): Body | null {
    tempVector2.set(touch.clientX, touch.clientY)
    tempVector3.copy(SceneUtils.screenToSceneSpace(camera, tempVector2, layerZ))
    tempVector3.divideScalar(MATTER_TO_THREE_SCALE)

    const collisions = Query.point(bodies, tempVector3)

    if (collisions.length > 0) {
      return collisions[0]
    } else {
      return null
    }
  }

  static getBodyType(body: Body): BodyType {
    if (body.label.toLowerCase().includes('coin')) {
      return BodyType.Coin
    } else if (body.label.toLowerCase().includes('collider')) {
      return BodyType.Collider
    } else if (body.label.toLowerCase().includes('slit')) {
      return BodyType.Slit
    }
    return BodyType.Unknown
  }

  static isCoin(body: Body): boolean {
    return this.getBodyType(body) === BodyType.Coin
  }

  static isCollider(body: Body): boolean {
    return this.getBodyType(body) === BodyType.Collider
  }

  static prepareModel(mesh: Mesh): { colliders: any[]; sensors: any[] } {
    const colliders: any[] = []
    const sensors: any[] = []

    mesh.traverse(child => {
      //child.name = child.name.replace(/\.?00[0-9]/g, '')

      /*const childMesh = child as Mesh
      const mat = childMesh.material as MeshBasicMaterial
      if (mat && mat.name) {
        mat.name = mat.name.replace(/\.?00[0-9]/g, '')
      }*/
      const type = child.userData.type
      const hide = child.userData.hide
      child.userData.enableBloom = false

      if (hide !== undefined && Number(hide) === 1) {
        child.visible = false
      }

      if (type === ColliderType.COLLIDER || type === ColliderType.SENSOR) {
        const category = Number(child.userData.colliderCategory)

        const m = child as Mesh

        m.material = colliderMaterial

        const options: IChamferableBodyDefinition = {
          isStatic: false,
          collisionFilter: { category: category as CollisionCategory },
          render: {
            lineWidth: 3,
            fillStyle: 'black'
          }
        }

        if (child.userData.action && child.userData.action !== '') options.label = child.userData.action

        const body = Bodies.rectangle(
          child.position.x * 100 * MATTER_SCALE,
          -child.position.y * 100 * MATTER_SCALE,
          child.scale.x * 100 * MATTER_SCALE,
          child.scale.z * 100 * MATTER_SCALE,
          options
        )

        if (type === ColliderType.COLLIDER) {
          body.isStatic = true
          body.label = child.name
          colliders.push({ mesh: child as Mesh, body: body })
        } else if (type === ColliderType.SENSOR) {
          body.isSensor = true
          sensors.push({ mesh: child as Mesh, body: body })
        }
      } /*else {
        if (child.type === 'Mesh') {
          child.receiveShadow = true
        }
      }*/
    })
    return { colliders: colliders, sensors: sensors }
  }
}
